import { queries, subscriptions } from '@/graphql'
import { apolloClient } from '@/graphql/client'
import { Draft, RequiredDocument } from '@/graphql/types'
import { RootState } from '@/store'
import { ApolloError, ApolloQueryResult } from 'apollo-client'
import { ToastProgrammatic } from 'buefy'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'

interface RequiredDocumentState {
  requiredDocuments: RequiredDocument[]
  requiredDocumentsSubscription: ZenObservable.Subscription | null
}

interface SetRequiredDocumentsInput {
  requiredDocuments: RequiredDocument[]
}

// Actions
export const FETCH_REQUIRED_DOCUMENTS = 'fetchRequiredDocuments'
export const SUBSCRIBE_REQUIRED_DOCUMENTS = 'subscribeRequiredDocuments'
export const CLEAR_REQUIRED_DOCUMENTS = 'clearRequiredDocuments'

// Mutations
const SET_REQUIRED_DOCUMENTS_SUBSCRIPTION = 'setRequiredDocumentsSubscription'
const SET_REQUIRED_DOCUMENTS = 'setRequiredDocuments'
// Getters
export const REQUIRED_DOCUMENTS = 'requiredDocuments'

const initialState: RequiredDocumentState = {
  requiredDocuments: [],
  requiredDocumentsSubscription: null
}

const state: RequiredDocumentState = { ...initialState }

const getters: GetterTree<RequiredDocumentState, RootState> = {
  [REQUIRED_DOCUMENTS]: ({ requiredDocuments }) => requiredDocuments
}

const actions: ActionTree<RequiredDocumentState, RootState> = {
  async [FETCH_REQUIRED_DOCUMENTS]({ commit, dispatch }, data) {
    const draftId = data?.draftId
    const organisationId = data?.organisationId

    if (!organisationId || !draftId) return

    return await apolloClient()
      .query({
        query: queries.GET_DRAFT_REQUIRED_DOCUMENTS,
        variables: { draftId, organisationId }
      })
      .then(({ data }: ApolloQueryResult<{ draft: Draft }>) => {
        const requiredDocuments = data.draft.requiredDocuments
        commit(SET_REQUIRED_DOCUMENTS, { requiredDocuments, draftId, organisationId })
        dispatch(SUBSCRIBE_REQUIRED_DOCUMENTS, { draftId, organisationId })
      })
      .catch(error => ToastProgrammatic.open({ message: error.message, type: 'is-danger' }))
  },
  [SUBSCRIBE_REQUIRED_DOCUMENTS]({ commit }, { draftId, organisationId }) {
    if (state.requiredDocumentsSubscription) {
      state.requiredDocumentsSubscription.unsubscribe()
    }
    const subscription = apolloClient()
      .subscribe({
        query: subscriptions.REQUIRED_DOCUMENTS_UPDATED_QUERY,
        variables: {
          draftId,
          organisationId
        }
      })
      .subscribe({
        next: ({ data: { draftUpdated } }) => {
          commit(SET_REQUIRED_DOCUMENTS, { requiredDocuments: draftUpdated.requiredDocuments, draftId, organisationId })
        },
        error: ({ message }: ApolloError) =>
          ToastProgrammatic.open({
            type: 'is-danger',
            message
          })
      })
    commit(SET_REQUIRED_DOCUMENTS_SUBSCRIPTION, subscription)
  },
  [CLEAR_REQUIRED_DOCUMENTS]({ commit }) {
    if (state.requiredDocumentsSubscription) {
      state.requiredDocumentsSubscription.unsubscribe()
    }
    commit(CLEAR_REQUIRED_DOCUMENTS)
  }
}

const mutations: MutationTree<RequiredDocumentState> = {
  [SET_REQUIRED_DOCUMENTS](state, requiredDocuments: SetRequiredDocumentsInput) {
    if (requiredDocuments) {
      state.requiredDocuments = requiredDocuments.requiredDocuments
    } else {
      state.requiredDocuments = []
    }
  },
  [SET_REQUIRED_DOCUMENTS_SUBSCRIPTION](state, subscription: ZenObservable.Subscription) {
    state.requiredDocumentsSubscription = subscription
  },
  [CLEAR_REQUIRED_DOCUMENTS](state) {
    state.requiredDocuments = initialState.requiredDocuments
    if (state.requiredDocuments) {
      state.requiredDocuments = []
    }
  }
}

const requiredDocuments: Module<RequiredDocumentState, RootState> = {
  namespaced: true,
  actions,
  getters,
  mutations,
  state
}

export default requiredDocuments
